import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  useFormik,
  FieldArray,
  FormikProvider,
  type FieldArrayRenderProps,
} from 'formik';
import AddIcon from '@mui/icons-material/Add';
import { string, object, array } from 'yup';
import { SupervisorInfo } from './SupervisorInfo';
import { roleTypeOptions } from './options';
import { type SupervisorFormValue } from './types';
import { PAGE_TEXT } from '../../lang';
import { SupervisorRoleTypes } from '../../../constants';
import {
  useUpdateNomination,
  type SupervisorValue,
} from '../../../graphql/NominationForm';
import {
  Select,
  Button,
  IconButton,
  Radio,
  Alert,
  type AlertDetails,
} from '../../../../../components';

type SupervisorTeamProps = {
  chiefSupervisor: SupervisorValue;
  isPendingCoSup: boolean;
  otherSupervisors: SupervisorValue[];
  refetchSupervisorTeam: () => void;
};

export const SupervisorTeamBox: React.FunctionComponent<SupervisorTeamProps> = (
  props: SupervisorTeamProps,
) => {
  const { nominationId } = useParams();
  const {
    chiefSupervisor,
    otherSupervisors,
    refetchSupervisorTeam,
    isPendingCoSup,
  } = props;
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const [openAlert, setOpenAlert] = useState(false);
  const [presentRole, setPresentRole] = useState<string>('');

  const [hasSupervisors, setHasSupervisors] = useState<boolean>(
    otherSupervisors.length > 0,
  );

  const handleSetSupervisor = (): void => {
    setHasSupervisors(!hasSupervisors);
  };

  const updateNomination = useUpdateNomination(refetchSupervisorTeam);
  const handleSubmitSupervisors = async (
    value: SupervisorFormValue,
  ): Promise<void> => {
    try {
      await updateNomination({
        variables: {
          id: nominationId,
          input: {
            supervisors: [chiefSupervisor, ...value.supervisors],
          },
        },
      });
      setAlertDetails({
        alertContent: 'Saved Supervisor Team Successfully.',
        alertType: 'success',
      });
      refetchSupervisorTeam();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setAlertDetails({
        alertContent: 'Saved Supervisor Team Failed.' + (e as Error).message,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  const formik = useFormik<SupervisorFormValue>({
    initialValues: {
      supervisors: otherSupervisors,
    },
    validationSchema: object({
      supervisors: array().of(
        object({
          type: string().required('Required'),
          title: string().required('Required'),
          jobTitle: string().required('Required'),
          givenName: string().required('Required'),
          surname: string().required('Required'),
          netId: string().when('type', {
            is: (type: string) =>
              type === SupervisorRoleTypes.CHIEF_SUPERVISOR ||
              type === SupervisorRoleTypes.CO_SUPERVISOR,
            then: schema => schema.required('Required'),
            otherwise: schema => schema.notRequired(),
          }),
          email: string().email('Invalid email format').required('Required'),
          department: string().required('Required'),
          institution: string().when('type', {
            is: (type: string) =>
              type === SupervisorRoleTypes.CHIEF_SUPERVISOR ||
              type === SupervisorRoleTypes.CO_SUPERVISOR,
            then: schema => schema.notRequired(),
            otherwise: schema => schema.required('Required'),
          }),
        }),
      ),
    }),
    onSubmit: handleSubmitSupervisors,
  });

  const addSupervisor =
    (arrayHelpers: FieldArrayRenderProps) =>
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const ifExternal =
        presentRole === SupervisorRoleTypes.EXTERNAL_CHIEF_SUPERVISOR ||
        presentRole === SupervisorRoleTypes.EXTERNAL_CO_SUPERVISOR;

      arrayHelpers.push({
        type: presentRole,
        title: '',
        netId: ifExternal ? undefined : '',
        jobTitle: '',
        givenName: '',
        surname: '',
        email: '',
        department: '',
        institution: '',
        previousSuccessfulSupervision: false,
        isDrcChair: false,
        isDoRPgS: false,
      });

      // MOD Jun 18, clear present role after add
      setPresentRole('');
    };

  const deleteSupervisor =
    (arrayHelpers: FieldArrayRenderProps, index: number): (() => void) =>
    () => {
      arrayHelpers.remove(index);
    };

  const handleChangePresentRole = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setPresentRole(event.target.value);
  };

  // RPG-[1450]
  // Add limitation to supervisory team members number to 10, exclude chief supervisor.
  const isDisableAddIcon = presentRole === '' || otherSupervisors.length > 10;

  return (
    <Container>
      <ContentBox>
        {PAGE_TEXT.SUPERVISOR_TEAM.ANY_SUPERVISOR}
        <div className="mt-4 mb-6">
          <Radio
            checked={hasSupervisors}
            onChange={handleSetSupervisor}
            label={PAGE_TEXT.YES}
          />
          <Radio
            checked={!hasSupervisors}
            onChange={handleSetSupervisor}
            label={PAGE_TEXT.NO}
          />
        </div>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} noValidate>
            {hasSupervisors && (
              <FieldArray
                name="supervisors"
                render={(arrayHelpers: FieldArrayRenderProps) => (
                  <>
                    {formik.values.supervisors.map(
                      (item: SupervisorValue, index) => {
                        return (
                          <SupervisorInfo
                            isPendingCoSup={isPendingCoSup}
                            key={`supervisor${index}`}
                            index={index}
                            handleDeleteSupervisor={deleteSupervisor(
                              arrayHelpers,
                              index,
                            )}
                          />
                        );
                      },
                    )}
                    <div className="select-role">
                      <div className="my-2">
                        {PAGE_TEXT.SUPERVISOR_TEAM.ROLE}
                      </div>
                      <span>
                        <Select
                          options={roleTypeOptions}
                          style={{ width: '80%' }}
                          value={presentRole}
                          onChange={handleChangePresentRole}
                        />
                        <IconButton
                          icon={
                            <AddIcon
                              style={{
                                backgroundColor: isDisableAddIcon
                                  ? '#7e7e7e'
                                  : '#951d1d',
                              }}
                            />
                          }
                          disabled={isDisableAddIcon}
                          onClick={addSupervisor(arrayHelpers)}
                        />
                      </span>
                      <h2
                        className={
                          isDisableAddIcon
                            ? 'hidden'
                            : 'p-1 ml-1 text-[#951D1D]'
                        }
                      >
                        {PAGE_TEXT.SUPERVISOR_TEAM.HINT}
                      </h2>
                    </div>
                  </>
                )}
              />
            )}
            <div className={hasSupervisors ? 'save-area' : 'hidden'}>
              <Button variant="contained" color="primary" type="submit">
                {PAGE_TEXT.SAVE}
              </Button>
            </div>
          </form>
        </FormikProvider>
      </ContentBox>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  padding-bottom: 2rem;
`;

const ContentBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 2rem;

  .select-role {
    width: 100%;

    span {
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    button {
      margin-left: 1rem;
    }

    button .MuiSvgIcon-root {
      background-color: #951d1d;
      color: #fff;
      border-radius: 50%;
      cursor: pointer;
    }
  }

  .save-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    margin-top: 4rem;
  }
`;
